// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/ChoiceDrill.jsx
//  ALTO RENDIMIENTO — el motor de los cuatro formatos deterministas
//
//  Un solo componente sirve a Tone Surgery, Subtexto, Precisión léxica y Oído
//  nativo. No es ahorro de trabajo: es que los cuatro son el MISMO ejercicio
//  cognitivo —tres opciones plausibles, una correcta, y la razón importa más
//  que el acierto— con distinto envoltorio. Cuatro componentes copiados serían
//  cuatro sitios donde arreglar el mismo bug.
//
//  Lo que cambia por formato lo declara `kind`:
//     scene  → situación + tres formas de decir lo mismo   (Tone Surgery)
//     line   → situación + la frase dicha + tres lecturas  (Subtexto)
//     blank  → frase con hueco + tres palabras             (Precisión léxica)
//     audio  → línea hablada con acento + pregunta         (Oído nativo)
//
//  ── LO QUE NO HAY AQUÍ, A PROPÓSITO ─────────────────────────────────────────
//  No hay reloj. El Dojo entrena velocidad; esta sala entrena precisión, y las
//  dos cosas no caben juntas: con prisa el alumno adivina, y adivinar no enseña
//  matiz. Por eso al fallar se muestran las TRES explicaciones, no sólo la de
//  la correcta — saber por qué las otras dos fallan es el ejercicio real.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { say, stop, isAvailable, ACCENTS } from './advancedSpeech';
import { shuffle } from './advancedBank';
import { guardaResultado } from './advancedApi';
import { textosSala } from './salaTextos';

const ROUNDS = 8;
const KEYS = ['A', 'B', 'C', 'D'];

// `idioma` lo trae la sala, que ya lo preguntó una vez para pintar las
// tarjetas. Si esta pantalla lo preguntara otra vez habría dos respuestas
// posibles para la misma cosa, y el día que no coincidieran el alumno entraría
// desde una tarjeta en inglés a una prueba en español.
export default function ChoiceDrill({ drill, onExit, idioma = 'es' }) {
  const S = textosSala(idioma);
  const T = S.drill;
  // El nombre del formato sale de la misma tabla que la tarjeta por la que
  // acaba de entrar. Antes salía de `advancedBank`, que sólo lo tiene en
  // español: la tarjeta decía «Lexical precision» y el título de dentro
  // «Precisión léxica».
  const nombre = S.formatos[drill.id]?.name || drill.name;
  const [deck] = useState(() => shuffle(drill.bank).slice(0, ROUNDS));
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [hits, setHits] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [plays, setPlays] = useState(0);
  const [fellBack, setFellBack] = useState(false);

  const item = deck[i];
  const isAudio = drill.kind === 'audio';
  const canSpeak = isAvailable();
  const answered = picked !== null;
  const startedAt = useRef(Date.now());
  const apuntado = useRef(false);

  // Las opciones se barajan una vez por reactivo: la correcta no cae siempre
  // en la misma posición, y no bailan mientras el alumno lee.
  //
  // ── EL `?.` NO ES PRUDENCIA, ES EL ARREGLO DE UN FALLO ────────────────────
  // Al contestar el último reactivo, `i` llega a `deck.length` y `deck[i]` pasa
  // a ser `undefined`. La pantalla de resultado está más abajo, detrás de un
  // `return` temprano — pero esto es un hook, y los hooks corren SIEMPRE, antes
  // de cualquier `return`. Así que `item.id` explotaba justo en el clic de «Ver
  // resultado →», y el alumno no llegaba a ver su marcador nunca.
  //
  // Los cuatro formatos que no usan IA comparten este componente, así que se
  // caían los cuatro. Estaba así desde que se escribieron: `56bf91a`.
  //
  // Las demás lecturas de `item` viven dentro de funciones, así que sólo corren
  // cuando alguien las llama y no hace falta tocarlas.
  const options = useMemo(
    () => shuffle(item?.options || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item?.id],
  );

  useEffect(() => stop, []);

  const play = useCallback((rate) => {
    if (!canSpeak) return;
    setPlaying(true);
    setPlays((p) => p + 1);
    say(
      item.line,
      { accent: item.accent || 'en-US', rate: rate ?? item.rate ?? 1.25 },
      {
        onEnd: () => setPlaying(false),
        onError: () => setPlaying(false),
        onFallback: () => setFellBack(true),
      },
    );
  }, [item, canSpeak]);

  // En Subtexto la frase también se oye: el tono es la mitad del mensaje.
  const speakLine = () => {
    if (!canSpeak || !item.line) return;
    setPlaying(true);
    say(item.line, { accent: 'en-US', rate: item.voice?.rate ?? 1.0 }, {
      onEnd: () => setPlaying(false),
      onError: () => setPlaying(false),
    });
  };

  const choose = (idx) => {
    if (answered) return;
    stop();
    setPicked(idx);
    if (options[idx].ok) setHits((h) => h + 1);
  };

  const next = () => {
    stop();
    setPicked(null);
    setPlays(0);
    setPlaying(false);
    setI((n) => n + 1);
  };

  /* ── Teclado ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (!answered) {
        const at = ['a', 'b', 'c', 'd'].indexOf(k);
        const num = parseInt(k, 10) - 1;
        if (at >= 0 && at < options.length) choose(at);
        else if (num >= 0 && num < options.length) choose(num);
      } else if (k === 'enter' || k === ' ') {
        e.preventDefault();
        if (i + 1 < deck.length) next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /* ── DEJAR CONSTANCIA ────────────────────────────────────────────────────
     Esta prueba se resuelve entera aquí, contra un banco con clave de
     respuesta: no pasa por el servidor y por eso funciona sin conexión y no
     cuesta un centavo. Pero hasta hoy tampoco DEJABA RASTRO — el alumno
     entrenaba, cerraba la pestaña, y era como si no hubiera pasado.

     Se manda al terminar y una sola vez. El `ref` es lo que garantiza esa
     única vez: este componente se vuelve a dibujar en cada tecla, y sin él
     cada repintado de la pantalla de resultado apuntaría otra sesión.

     Si falla, se calla. Perder el apunte es molesto; quitarle al alumno su
     resultado de la pantalla porque el servidor no contestó, no.               */
  useEffect(() => {
    if (i < deck.length || apuntado.current) return;
    apuntado.current = true;
    guardaResultado({
      formato: drill.id,
      aciertos: hits,
      intentos: deck.length,
      segundos: (Date.now() - startedAt.current) / 1000,
    }).catch(() => { /* el resultado ya está en pantalla */ });
  }, [i, deck.length, hits, drill.id]);

  /* ── Resultado ─────────────────────────────────────────────────────────── */
  if (i >= deck.length) {
    const pct = Math.round((hits / deck.length) * 100);
    const mins = Math.max(1, Math.round((Date.now() - startedAt.current) / 60000));
    return (
      <div className="ar-result ar-screen" style={{ '--ar-mc': drill.accent }}>
        <span className="ar-lbl">{nombre}</span>
        <p className="ar-grade ar-num">{hits}/{deck.length}</p>
        <span className="ar-lbl">{T.aciertos(pct)}</span>

        <div className="ar-recap">
          <div><b className="ar-num">{hits}</b><span>{T.correctas}</span></div>
          <div><b className="ar-num">{deck.length - hits}</b><span>{T.falladas}</span></div>
          <div><b className="ar-num">{mins}</b><span>{T.minutos}</span></div>
        </div>

        <p className="ar-lead">{pct >= 75 ? T.cierreBueno : T.cierreFlojo}</p>

        <div className="ar-actions">
          <button type="button" className="ar-btn" onClick={() => window.location.reload()}>{T.otraRonda}</button>
          <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>{T.volverSala}</button>
        </div>
      </div>
    );
  }

  const won = answered && options[picked].ok;

  return (
    <div className="ar-screen" style={{ '--ar-mc': drill.accent }}>
      <div className="ar-hud">
        <button type="button" className="ar-quit" onClick={() => { stop(); onExit(); }}>{T.salir}</button>
        <span className="ar-badge">{nombre}</span>
        <span className="ar-spacer" />
        <span className="ar-badge">{item.level}</span>
        <span className="ar-count">{hits}</span>
      </div>

      <div className="ar-track">
        <i style={{ width: `${(i / deck.length) * 100}%` }} />
      </div>

      <div className="ar-card-q">
        {/* ── Oído nativo: primero se escucha ── */}
        {isAudio ? (
          <div className="ar-audio">
            <span className="ar-accent">
              {ACCENTS[item.accent]?.flag}{' '}
              {idioma === 'en' ? ACCENTS[item.accent]?.label_en : ACCENTS[item.accent]?.label}
              {' · '}{T.velocidadReal}
            </span>
            <button
              type="button"
              className={`ar-play ${playing ? 'ar-on' : ''}`}
              onClick={() => play()}
              disabled={!canSpeak}
              aria-label={T.escuchar}
            >
              ▶
            </button>
            {plays > 0 && !answered && (
              <button type="button" className="ar-slow" onClick={() => play(0.85)}>
                {T.masDespacio}
              </button>
            )}
            {answered && <p className="ar-heard">“{item.line}”</p>}
            {fellBack && (
              <p className="ar-note">
                {/* El `|| ''` va FUERA del ternario. Dentro sólo cubría una de
                    las dos ramas, así que en inglés y con un acento que no
                    estuviera en la tabla, `.toLowerCase()` reventaba — y este
                    aviso sale justo cuando algo ya ha ido mal con las voces. */}
                {T.vozSuplente(((idioma === 'en'
                  ? ACCENTS[item.accent]?.label_en
                  : ACCENTS[item.accent]?.label) || '').toLowerCase())}
              </p>
            )}
            <p className="ar-q">{item.q}</p>
          </div>
        ) : (
          <>
            <span className="ar-lbl">
              {drill.kind === 'blank' ? T.completa : T.laSituacion}
            </span>
            <p className="ar-scene">{item.scene}</p>

            {drill.kind === 'line' && (
              <>
                <p className="ar-line">“{item.line}”</p>
                {canSpeak && (
                  <button type="button" className="ar-slow" style={{ marginTop: '.8rem' }}
                          onClick={speakLine} disabled={playing}>
                    {T.escucharTono}
                  </button>
                )}
              </>
            )}

            {drill.kind === 'blank' && (
              <p className="ar-blank">
                {item.base.split('___')[0]}
                <b>______</b>
                {item.base.split('___')[1]}
              </p>
            )}
          </>
        )}
      </div>

      <div className="ar-opts">
        {options.map((o, idx) => {
          const state = !answered ? ''
            : o.ok ? ' ar-opt-ok'
            : picked === idx ? ' ar-opt-no'
            : ' ar-opt-off';
          return (
            <button
              key={o.text}
              type="button"
              className={`ar-opt${state}`}
              onClick={() => choose(idx)}
              disabled={answered}
            >
              <span className="ar-opt-k">{KEYS[idx]}</span>
              <span>{o.text}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <>
          {/* Subtexto y Oído nativo traen UNA explicación del reactivo entero.
              Tone y Precisión léxica traen una por opción: ahí el aprendizaje
              está en ver por qué fallan las otras dos, no sólo cuál acertó. */}
          {item.why ? (
            <div className="ar-why">
              <b>{won ? T.correcto : T.incorrecto}</b>{item.why}
            </div>
          ) : (
            <div className="ar-why-all">
              {options.map((o) => (
                <div key={o.text} className={`ar-why-row ${o.ok ? 'ar-r-ok' : 'ar-r-no'}`}>
                  <q>{o.text}</q> — {o.why}
                </div>
              ))}
            </div>
          )}

          <div className="ar-actions">
            <button type="button" className="ar-btn" onClick={next}>
              {i + 1 >= deck.length ? T.verResultado : T.siguiente}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
